import type {
  IndexNode,
  IndexRequestProgressStep,
  LcRouteParams,
  PackageAliasSummary,
} from './types';

const INDEXING_PROGRESS_STEPS: IndexRequestProgressStep[] = [
  'fetching',
  'triaging',
  'summarizing',
  'ast_summarizing',
  'storing',
  'code_graph',
  'embedding',
  'vector_storing',
];

const INDEXING_PROGRESS_LABELS: Record<IndexRequestProgressStep, string> = {
  fetching: 'Fetching package',
  triaging: 'Triaging files',
  summarizing: 'Summarizing files',
  ast_summarizing: 'AST summarization',
  storing: 'Persisting package',
  code_graph: 'Extracting triples',
  embedding: 'Embedding nodes',
  vector_storing: 'Uploading vectors',
  // Terminal states — never shown as "Step x/y" (they're not in
  // INDEXING_PROGRESS_STEPS) but required for exhaustive indexing.
  done: 'Done',
  error: 'Error',
};

export function formatIndexedAt(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

export function relativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  if (Number.isNaN(then)) return dateStr;
  const diffMs = now - then;
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatIndexedAt(dateStr);
}

export function countIndexNodes(node: IndexNode): number {
  return 1 + node.children.reduce((total, child) => total + countIndexNodes(child), 0);
}

export function buildTreeRoute(params: LcRouteParams): string {
  return `/${encodeURIComponent(params.registry)}/${encodeURIComponent(params.name)}/${encodeURIComponent(params.version)}/tree`;
}

export function buildIndexTreeRoute(params: LcRouteParams): string {
  return `/${encodeURIComponent(params.registry)}/${encodeURIComponent(params.name)}/${encodeURIComponent(params.version)}/browse`;
}

export function registryLabel(registry: string): string {
  if (registry === 'pypi') return 'PyPI';
  if (registry === 'npm') return 'npm';
  if (registry === 'pub') return 'pub.dev';
  if (registry === 'crates') return 'crates.io';
  if (registry === 'maven') return 'Maven Central';
  if (registry === 'github') return 'GitHub';
  if (registry === 'gitlab') return 'GitLab';
  if (registry === 'local') return 'Local';
  return registry;
}

export function formatAliasLabel(alias: PackageAliasSummary): string {
  return `${registryLabel(alias.registry)} ${alias.name}`;
}

// Maven packages are identified by `groupId:artifactId` coordinates (e.g.
// "com.google.guava:guava"). The groupId is noise for display purposes —
// show only the artifactId, the part users actually recognize.
export function displayPackageName(registry: string, name: string): string {
  if (registry !== 'maven') return name;
  const idx = name.lastIndexOf(':');
  return idx === -1 ? name : name.slice(idx + 1);
}

// Prefer the shortest alias — usually the most recognizable display name.
// Shared so the catalog card and the recently-indexed list show one consistent
// name for the same package.
export function primaryAlias(p: {
  aliases?: PackageAliasSummary[] | null;
}): PackageAliasSummary | null {
  const list = p.aliases ?? [];
  if (list.length === 0) return null;
  return list.slice().sort((a, b) => a.name.length - b.name.length)[0];
}

export function packageLanguageLabel(languages: string[]): string {
  const normalized = new Set(languages.map((language) => language.toLowerCase()));
  if (normalized.has('typescript') && normalized.has('javascript')) return 'JS / TS';
  if (normalized.has('typescript')) return 'TypeScript';
  if (normalized.has('javascript')) return 'JavaScript';
  if (normalized.has('python')) return 'Python';
  if (normalized.has('dart')) return 'Dart';
  const first = languages[0];
  if (!first) return 'Code';
  return first.charAt(0).toUpperCase() + first.slice(1);
}

// Per-language identity colors (GitHub-linguist palette) so languages separate
// at a glance. Color lives ONLY in the small dot — badge chrome stays neutral
// to fit the monochrome theme. Literal class strings so Tailwind JIT sees them.
const LANGUAGE_DOT_CLASSES: Record<string, string> = {
  python: 'bg-[#3572A5]',
  javascript: 'bg-[#F1E05A]',
  typescript: 'bg-[#3178C6]',
  dart: 'bg-[#00B4AB]',
  rust: 'bg-[#DEA584]',
  java: 'bg-[#B07219]',
  kotlin: 'bg-[#A97BFF]',
  go: 'bg-[#00ADD8]',
  swift: 'bg-[#F05138]',
  csharp: 'bg-[#178600]',
  cpp: 'bg-[#F34B7D]',
  c: 'bg-[#555555]',
  ruby: 'bg-[#701516]',
  php: 'bg-[#4F5D95]',
  scala: 'bg-[#C22D40]',
  elixir: 'bg-[#6E4A7E]',
};

export function languageDot(languages: string[]): string {
  return singleLanguageDotClass(languages[0] ?? '');
}

export function singleLanguageDotClass(language: string): string {
  return LANGUAGE_DOT_CLASSES[language?.toLowerCase()] ?? 'bg-dim';
}

export function singleLanguageBadgeClasses(_language: string): string {
  // Monochrome theme: neutral badge for every language.
  return 'border-rule bg-bg text-link';
}

export function aliasRegistryPrefixClasses(_registry: string): string {
  // Monochrome theme: registry is a category, not a status — use a neutral chip.
  return 'bg-accent-soft text-dim';
}

export function languageBadgeClasses(_languages: string[]): string {
  // Monochrome theme: neutral badge for every language set.
  return 'border-rule bg-bg text-link';
}

export function buildQueryRoute(params: LcRouteParams): string {
  return `/${encodeURIComponent(params.registry)}/${encodeURIComponent(params.name)}/${encodeURIComponent(params.version)}/query`;
}

export function buildRawRoute(params: LcRouteParams): string {
  return `/${encodeURIComponent(params.registry)}/${encodeURIComponent(params.name)}/${encodeURIComponent(params.version)}/raw`;
}

const CITATION_PATTERN = /\[(@?[a-zA-Z][\w\-./]*@[^\s[\]]+)\](?!\()/g;

// Wraps source citations like "[pkg@version/path]" in inline code so they
// render as styled chips rather than bare brackets. Skips anything that
// looks like a markdown link (followed by `(`).
export function formatAnswerCitations(md: string): string {
  return md.replace(CITATION_PATTERN, '`$1`');
}

// Extracts unique source citations from an answer string, in first-seen order.
// Used to render a dedicated "Sources" section separate from the answer body.
export function extractAnswerCitations(md: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const match of md.matchAll(CITATION_PATTERN)) {
    const ref = match[1];
    if (!seen.has(ref)) {
      seen.add(ref);
      out.push(ref);
    }
  }
  return out;
}

// Formats an ask-duration ms as "1.2s" or "150ms".
export function formatAskElapsed(ms: number): string {
  if (ms < 1000) return `${Math.max(0, Math.round(ms))}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

// Formats an indexing-job duration ms as "45s", "2m 14s", or "1h 12m" — the
// multi-minute range formatAskElapsed (built for sub-2s ask latency) doesn't cover.
export function formatDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

export function formatIndexingProgress(
  step: IndexRequestProgressStep | null,
  message?: string | null
): string | null {
  const base = message?.trim() || null;
  if (!step) return base;

  const stepIndex = INDEXING_PROGRESS_STEPS.indexOf(step);
  if (stepIndex === -1) return base;

  const label = `Step ${stepIndex + 1}/${INDEXING_PROGRESS_STEPS.length}: ${INDEXING_PROGRESS_LABELS[step]}`;
  return base ? `${label} - ${base}` : label;
}
/**
 * A docs-only index (no package source was indexed — see judgeIndexQuality's
 * deterministic cap) is a degraded state regardless of its numeric score:
 * render it red with an explicit label wherever quality is shown.
 */
export function isDocsOnlyQuality(note: string | null | undefined): boolean {
  return !!note && note.startsWith('DOCS-ONLY');
}
